import VueNativeSock from 'vue-native-websocket'
import Vue from 'vue'
import { RECEIPT_CONFIG } from '@/config/receipt'

let wsInitialized = false

class ReceiptService {
  constructor() {
    this.socket = null
    this.isConnected = false
  }

  initWebSocket() {
    if (wsInitialized || !process.env.VUE_APP_ENABLE_PRINTER) {
      return
    }

    try {
      Vue.use(VueNativeSock, process.env.VUE_APP_PRINTER_URL, {
        reconnection: true,
        reconnectionAttempts: RECEIPT_CONFIG.WEBSOCKET.RECONNECTION_ATTEMPTS,
        reconnectionDelay: RECEIPT_CONFIG.WEBSOCKET.RECONNECTION_DELAY_MS
      })
      wsInitialized = true
    } catch (error) {
      // WebSocket initialization failed
    }
  }

  async fetchReceipt(invoiceId, httpClient) {
    if (!invoiceId) {
      throw new Error('Invoice ID tidak ditemukan')
    }

    if (!httpClient) {
      throw new Error('HTTP client tidak tersedia')
    }

    try {
      const response = await httpClient.get(
        `${process.env.VUE_APP_API_URL}/pos/invoices/${invoiceId}/receipt`
      )

      if (response.status === 200) {
        return this.transformReceiptData(response.data.data)
      } else {
        throw new Error('Gagal memuat data struk')
      }
    } catch (error) {
      if (error.response) {
        throw new Error('Gagal memuat struk dari server')
      } else if (error.request) {
        throw new Error('Tidak dapat terhubung ke server')
      } else {
        throw new Error('Terjadi kesalahan saat memuat struk')
      }
    }
  }

  async fetchLastReceipt(httpClient) {
    if (!httpClient) {
      throw new Error('HTTP client tidak tersedia')
    }

    try {
      const response = await httpClient.get(
        `${process.env.VUE_APP_API_URL}/pos/transactions/last-receipt`
      )

      if (response.status === 200) {
        return this.transformReceiptData(response.data.data)
      } else {
        throw new Error('Gagal memuat data struk')
      }
    } catch (error) {
      if (error.response) {
        throw new Error('Gagal memuat struk dari server')
      } else if (error.request) {
        throw new Error('Tidak dapat terhubung ke server')
      } else {
        throw new Error('Terjadi kesalahan saat memuat struk')
      }
    }
  }

  async printReceipt(receiptData, httpClient, socket) {
    if (!receiptData) {
      throw new Error('Data struk tidak tersedia')
    }

    if (!receiptData.invoice) {
      throw new Error('Data invoice tidak tersedia')
    }

    if (process.env.VUE_APP_ENABLE_PRINTER) {
      // Check if we have invoice ID - if not, try using last-receipt endpoint
      if (receiptData.invoice && receiptData.invoice.id) {
        await this.printViaWebSocket(receiptData.invoice.id, httpClient, socket)
      } else {
        // Fallback: use last-receipt endpoint for printing
        await this.printLastReceiptViaWebSocket(httpClient, socket)
      }
    } else {
      this.printViaBrowser()
    }
  }

  async printViaWebSocket(invoiceId, httpClient, socket) {
    if (!httpClient) {
      throw new Error('HTTP client tidak tersedia')
    }

    if (!invoiceId) {
      throw new Error('Invoice ID tidak tersedia untuk printing')
    }

    try {
      this.initWebSocket()

      const response = await httpClient.get(
        `${process.env.VUE_APP_API_URL}/pos/invoices/${invoiceId}/receipt/json`
      )

      if (response.status === 200) {
        const receiptJson = JSON.stringify(response.data.data.Receipt)

        // Wait a bit for WebSocket to be ready
        await new Promise(resolve => setTimeout(resolve, 100))

        if (socket) {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(receiptJson)
          } else {
            throw new Error('WebSocket tidak terhubung')
          }
        } else if (Vue.prototype.$socket) {
          Vue.prototype.$socket.send(receiptJson)
        } else {
          throw new Error('WebSocket tidak tersedia')
        }
      } else {
        throw new Error('Gagal mendapatkan data untuk printer')
      }
    } catch (error) {
      throw new Error('Gagal mengirim ke printer: ' + error.message)
    }
  }

  async printLastReceiptViaWebSocket(httpClient, socket) {
    if (!httpClient) {
      throw new Error('HTTP client tidak tersedia')
    }

    try {
      this.initWebSocket()

      const response = await httpClient.get(
        `${process.env.VUE_APP_API_URL}/pos/transactions/last-receipt/endpoint/json`
      )

      if (response.status === 200) {
        const receiptJson = JSON.stringify(response.data.data.Receipt)

        // Wait a bit for WebSocket to be ready
        await new Promise(resolve => setTimeout(resolve, 100))

        if (socket) {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(receiptJson)
          } else {
            throw new Error('WebSocket tidak terhubung')
          }
        } else if (Vue.prototype.$socket) {
          Vue.prototype.$socket.send(receiptJson)
        } else {
          throw new Error('WebSocket tidak tersedia')
        }
      } else {
        throw new Error('Gagal mendapatkan data untuk printer')
      }
    } catch (error) {
      throw new Error('Gagal mengirim ke printer: ' + error.message)
    }
  }

  printViaBrowser() {
    // Trigger browser print dialog
    setTimeout(() => {
      window.print()
    }, RECEIPT_CONFIG.PRINT_DELAY_MS)
  }

  transformReceiptData(apiData) {
    if (!apiData) {
      throw new Error('Data struk tidak valid')
    }

    // Ensure invoice object is properly preserved with all fields
    const invoice = apiData.invoice || {}

    // Validate that invoice has required fields
    if (!invoice.id) {
      // Log warning but don't throw - might be valid in some cases
      if (process.env.NODE_ENV === 'development') {
        console.warn('Receipt data missing invoice.id:', apiData)
      }
    }

    return {
      outlet: apiData.outlet || {},
      invoice: invoice, // Preserve full invoice object
      transaction: apiData.transaction || {},
      transactionItems:
        apiData.transaction_items || apiData.transactionItems || [],
      payments: apiData.payment || apiData.payments || [],
      vouchers: apiData.voucher || apiData.vouchers || [],
      cashier: apiData.cashier || {},
      salesPerson: apiData.sales_person || apiData.salesPerson || {}
    }
  }

  calculateReceiptTotals(receiptData) {
    const { transactionItems, transaction, vouchers, payments } = receiptData

    let totalQty = 0
    let subTotalPrice = 0
    let totalAllDiscount = 0
    let diskonItemTotal = 0
    let specialDiscount = 0

    // Calculate transaction items
    for (const item of transactionItems) {
      totalQty += item.qty || 0

      const subTotalTemp = (item.qty || 0) * (item.init_price || 0)
      subTotalPrice += subTotalTemp

      const sDiscount =
        parseFloat(item.qty || 0) * parseFloat(item.special_discount || 0)
      specialDiscount += parseFloat(sDiscount)

      const diskonItem =
        parseFloat(item.discount || 0) * parseFloat(item.qty || 0)
      diskonItemTotal += parseFloat(diskonItem)

      const sumDiscount =
        parseFloat(item.qty || 0) * parseFloat(item.discount || 0)
      totalAllDiscount += parseFloat(sumDiscount)
    }

    const discountTransaction = parseFloat(transaction.discount || 0)
    const infaq = transaction.infaq || 0
    const roundUp = parseInt(transaction.round_up || 0)

    // Calculate payment totals
    let totalPaymentAmount = 0
    for (const payment of payments) {
      totalPaymentAmount += parseFloat(payment.total_amount || 0)
    }

    // Calculate voucher amount
    let voucherPaymentMethod = 0
    if (vouchers.length > 0) {
      voucherPaymentMethod = parseFloat(vouchers[0].total_amount || 0)
    }

    // Calculate change (kembalian)
    const grandTotal =
      subTotalPrice -
      totalAllDiscount -
      specialDiscount -
      discountTransaction -
      voucherPaymentMethod

    let kembalian = 0
    if (payments.length > 0 && payments[0].is_cash > 0) {
      kembalian = totalPaymentAmount - grandTotal - infaq + parseFloat(roundUp)
    }

    if (vouchers.length > 0 && voucherPaymentMethod > 0) {
      if (payments.length > 0 && payments[0].is_cash > 0) {
        kembalian += voucherPaymentMethod
      }
    }

    return {
      totalQty,
      subTotalPrice,
      totalAllDiscount,
      diskonItemTotal,
      specialDiscount,
      discountTransaction,
      infaq,
      roundUp,
      totalPaymentAmount,
      voucherPaymentMethod,
      kembalian,
      grandTotal
    }
  }

  formatCashierName(name) {
    if (!name) return ''
    // Remove obfuscation - display actual name
    return name
  }

  formatSalesPersonName(name) {
    if (!name) return ''
    // Remove obfuscation - display actual name
    return name
  }
}

// Create singleton instance
const receiptService = new ReceiptService()

export default receiptService
